import React, { FC } from "react";
import { css } from "@emotion/react";
import theme from "@styles/theme";
import { LABEL_PLACEMENT, STYLE_TYPE } from "baseui/checkbox";
import { OnChangeParams, Value } from "baseui/select";
import { Issue, IssueType } from "@store/issue/types";
import Select from "@components/base/select";
import Input from "@components/base/input";
import TextareaInput from "@components/base/text-area";
import Checkbox from "@components/base/checkbox";
import { overrideButtonStyles } from "@components/base/button/overrides";
import Button from "@components/base/button";
import { useTranslation } from "react-i18next";
import Table from "@components/base/table";
import { User } from "@store/user/types";
import { RecurringIssueTypes } from "@screens/RecurringIssues";

interface NewIssueFormProps {
  // location Selection
  onSelectLocation: (value: OnChangeParams) => void;
  locations: Value;
  selectedLocations?: Value;
  //   Table edits
  isInEditMode: boolean;
  activeIssues?: string[][];
  baseIssue: Pick<Issue, "label" | "reportedBy" | "description">;
  issueType?: IssueType;
  changeType?: (value: IssueType) => void;
  onBaseIssueChange: (
    issue: Pick<Issue, "label" | "reportedBy" | "description">,
  ) => void;
  user?: User;
  priority: boolean;
  onTogglePriority?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  isRecurringIssue: boolean;
  children?: React.ReactNode;
}
const NewIssueForm: FC<NewIssueFormProps> = ({
  onSelectLocation,
  locations,
  selectedLocations,
  isInEditMode,
  activeIssues = [],
  baseIssue,
  issueType = IssueType.TECHNICAL,
  changeType,
  onBaseIssueChange,
  user,
  priority,
  onTogglePriority,
  onSubmit,
  canSubmit,
  isRecurringIssue,
  children = null,
}) => {
  const { t } = useTranslation();

  const types = RecurringIssueTypes.slice(1);
  const selectedType =
    types.find((option) => option.id === issueType) || types[0];
  const showTypeSelect = !!changeType;

  const handleTypeChange = ({ value }: OnChangeParams) => {
    // We only expect IssueType to be passed as value
    if (changeType) changeType(value[0].id as IssueType);
  };
  return (
    <form
      data-test-id="new-issue-screen"
      css={styles.container}
      onSubmit={(e) => e.preventDefault()}>
      <div css={styles.row}>
        <div
          css={[
            styles.leftContainer,
            { borderRightColor: theme.colors.contentInverseSecondary },
          ]}>
          {showTypeSelect && (
            <div style={{ marginBottom: theme.sizing.scale800 }}>
              <Select
                label={t("tasks.type")}
                required
                options={types}
                value={[selectedType]}
                onChange={handleTypeChange}
                openInModal
              />
            </div>
          )}
          <div data-test-id="location-section" css={styles.locationSection}>
            <Select
              label={t("common.property")}
              required
              props={{
                placeholder: t("search.location"),
              }}
              options={locations}
              value={selectedLocations}
              multi={isRecurringIssue}
              onChange={onSelectLocation}
              openInModal
              overrides={{
                ValueContainer: {
                  style: {
                    maxHeight: "250px",
                    overflowY: "auto",
                  },
                },
                ControlContainer: {
                  style: {
                    backgroundColor: theme.colors.white,
                  },
                },
                Popover: {
                  props: {
                    placement: "bottom",
                    overrides: {
                      Body: {
                        style: {
                          maxHeight: "475px",
                          zIndex: 99999,
                        },
                      },
                    },
                  },
                },
                Tag: {
                  props: {
                    overrides: {
                      Root: {
                        style: () => ({
                          backgroundColor: "transparent",
                          border: `1px solid ${theme.colors.buttonDisabledFill}`,
                        }),
                      },
                      Text: {
                        style: () => ({
                          color: "black",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }),
                      },
                      Action: {
                        style: () => ({
                          color: "gray",
                          transform: "scale(0.8)",
                        }),
                      },
                    },
                  },
                },
                IconsContainer: {
                  style: () => ({
                    display: "flex",
                    alignSelf: "flex-start",
                    alignItems: "center",
                    paddingTop: "10px",
                  }),
                },
                Dropdown: {
                  style: {
                    backgroundColor: theme.colors.white,
                    height: "475px",
                  },
                },
                DropdownListItem: {
                  style: {
                    backgroundColor: theme.colors.white,
                    borderBottom: `1px solid ${theme.colors.contentInverseSecondary}`,
                    height: "35px",
                    display: "flex",
                    alignItems: "center",
                  },
                },
                StatefulMenu: {
                  props: {
                    overrides: {
                      OptgroupHeader: {
                        style: {
                          backgroundColor: theme.colors.mono200,
                          fontSize: "16px",
                          fontWeight: 600,
                          paddingLeft: "20px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </div>
          {!isInEditMode && !!location && activeIssues.length > 0 && (
            <>
              <label>{t("issue.newIssueModal.createdIssuesTitle")}:</label>
              <Table
                overrides={{
                  Root: {
                    style: {
                      marginBottom: theme.sizing.scale800,
                      maxHeight: "175px",
                      maxWidth: "575px",
                    },
                  },
                  TableBodyCell: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                      whiteSpace: "nowrap",
                      overflow: "auto",
                    },
                  },
                  TableEmptyMessage: {
                    style: {
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                  },
                  TableHeadCell: {
                    style: {
                      backgroundColor: theme.colors.mono200,
                      fontWeight: "bold",
                      fontSize: "11px",
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                  },
                }}
                columns={[
                  t("issue.newIssueModal.columns.date"),
                  t("issue.newIssueModal.columns.task"),
                  t("issue.newIssueModal.columns.status"),
                ]}
                data={activeIssues}
              />
            </>
          )}
          <div data-test-id="new-issue-task">
            <Input
              label={t("common.issue")}
              required
              value={baseIssue.label}
              placeholder={t("issue.typeTaskName")}
              onChangeText={(label) =>
                onBaseIssueChange({ ...baseIssue, label })
              }
              containerStyle={{
                marginBottom: theme.sizing.scale800,
                width: "100%",
              }}
            />
          </div>
          <Input
            label={t("issue.createdBy")}
            value={user?.profile.name}
            containerStyle={{
              marginBottom: theme.sizing.scale800,
              width: "100%",
            }}
            disabled
          />
          <div data-test-id="new-issue-reporter">
            <Input
              label={t("issue.reportedBy")}
              required
              value={baseIssue.reportedBy}
              placeholder={t("issue.typeReporterName")}
              onChangeText={(reportedBy) =>
                onBaseIssueChange({ ...baseIssue, reportedBy })
              }
              containerStyle={{
                marginBottom: theme.sizing.scale800,
                width: "100%",
              }}
            />
          </div>
          <div data-test-id="new-issue-remark">
            <TextareaInput
              label={`${t("common.remark")}(${t("common.optional")})`}
              value={baseIssue.description ?? ""}
              placeholder={t("issue.addRemark")}
              onChangeText={(description) =>
                onBaseIssueChange({ ...baseIssue, description })
              }
            />
          </div>
        </div>

        <div css={styles.rightContainer}>
          {children}
          <div>
            {!isRecurringIssue && (
              <Checkbox
                checked={priority}
                checkmarkType={STYLE_TYPE.toggle_round}
                labelPlacement={LABEL_PLACEMENT.right}
                onChange={onTogglePriority}
                overrides={{
                  Root: {
                    style: {
                      marginBottom: "24px",
                    },
                  },
                }}>
                {t("issue.priority")}
              </Checkbox>
            )}
            <Button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              overrides={overrideButtonStyles({
                BaseButton: {
                  width: "100%",
                  alignSelf: "flex-end",
                },
              })}>
              {isInEditMode ? t("issue.change") : t("issue.create")}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

const styles = {
  container: css({
    width: "55vw",
    backgroundColor: theme.colors.white,
    boxSizing: "border-box",
    textAlign: "left",
    justifyContent: "space-between",
  }),
  buttonContainer: css({
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.sizing.scale800,
    "& button": {
      fontFamily: "SourceSansPro-Bold",
    },
  }),
  header: css({
    marginTop: theme.sizing.scale1200,
    marginBottom: theme.sizing.scale1600,
  }),
  row: css({
    display: "flex",
    flexDirection: "row",
    marginBottom: theme.sizing.scale400,
  }),
  leftContainer: css({
    flex: 1,
    paddingRight: theme.sizing.scale800,
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: theme.colors.contentInverseSecondary,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  }),
  rightContainer: css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
    marginTop: 20,
    paddingLeft: theme.sizing.scale800,
  }),
  checkbox: css({
    width: 24,
    heigth: 24,
    marginRight: theme.sizing.scale400,
  }),
  locationSection: css({
    marginBottom: theme.sizing.scale800,
  }),
};

export default NewIssueForm;
